import React from 'react';
import { useSelector } from 'react-redux';
import { isPremiumOrPro } from '../../utils/SubUtils';
import LockedFeature from './LockedFeature';


const ShareHoldings = ({ data }) => {
    const user = useSelector((state) => state.auth.user);
    const subscription = user?.user_subscription;
    const isPro = isPremiumOrPro(subscription);

    if (!isPro) {
        return (
            <LockedFeature 
                title="Shareholdings Data Locked" 
                description={<span>Upgrade to <strong>Premium or Pro</strong> plan to unlock institutional and insider holdings data.</span>} 
            />
        );
    }

    if (!data) return <div className="p-5 text-center">Loading data...</div>;

    const {
        total_investors,
        shares_held_by_strategic_investors,
        outstanding_shares,
        free_float,
        top_20_investors = [],
        industry,
        country_name,
        market_cap,
        strategic_ownership,
        free_float_percentage
    } = data;
    
    // Helper to format numbers
    const formatNumber = (num) => {
        if (!num && num !== 0) return "-";
        if (num >= 1.0e+9) return (num / 1.0e+9).toFixed(2) + "B";
        if (num >= 1.0e+6) return (num / 1.0e+6).toFixed(2) + "M";
        if (num >= 1.0e+3) return (num / 1.0e+3).toFixed(2) + "K";
        return num.toLocaleString();
    }

    const formatCurrency = (num) => {
        if (!num && num !== 0) return "-";
         if (num >= 1.0e+9) return "$" + (num / 1.0e+9).toFixed(2) + "B";
        if (num >= 1.0e+6) return "$" + (num / 1.0e+6).toFixed(2) + "M";
        return "$" + num.toLocaleString();
    }

    const calcPercent = (held) => {
        if (!held || !outstanding_shares) return 0;
        return ((held / outstanding_shares) * 100).toFixed(2);
    };
    return (
       <div className="stock-dashboard-wrapper mt-4 mb-5 px-3">
       <div className="row g-3">
        {/* Card 1: Total Investors */}
        <div className="col-12 col-md-6 col-lg-3 ">
            <div className="metric-card p-3 rounded-4 border shadow-sm custom-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className=" small">Total Investors</span>
                    <img src="/images/MI/users.svg" alt="Total Investors" width="20" height="20" className="opacity-75" />
                </div>
                <h3 className="h4 mb-1">{total_investors ? total_investors.toLocaleString() : 'No Change '}</h3>
                <p className=" small mb-0">{data.total_investors_change_percentage_1month ? (data.total_investors_change_percentage_1month > 0 ? '+' : '-') + data.total_investors_change_percentage_1month + '%' : 'No Change'} from last month</p>
            </div>
        </div>

        {/* Card 2: Institutional Ownership */}
        <div className="col-12 col-md-6 col-lg-3 ">
            <div className="metric-card p-3 rounded-4 border shadow-sm custom-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className=" small">Strategic Investors (Shares)</span>
                    <img src="/images/MI/dollar-sign.svg" alt="Institutional Ownership" width="20" height="20" className="opacity-75" />
                </div>
                <h3 className="h4 mb-1">{formatNumber(shares_held_by_strategic_investors)}</h3>
                <p className=" small mb-0">{data.shares_held_by_strategic_investors_change_percentage_1month ? (data.shares_held_by_strategic_investors_change_percentage_1month > 0 ? '+' : '-') + data.shares_held_by_strategic_investors_change_percentage_1month + '%' : 'No Change'} from last month</p>
            </div>
        </div>

        {/* Card 3: Shares Outstanding */}
        <div className="col-12 col-md-6 col-lg-3 ">
            <div className="metric-card p-3 rounded-4 border shadow-sm custom-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className=" small">Shares Outstanding</span>
                    <img src="/images/MI/credit-card.svg" alt="Shares Outstanding" width="20" height="20" className="opacity-75" />
                </div>
                <h3 className="h4 mb-1">{formatNumber(outstanding_shares)}</h3>
                <p className=" small mb-0">{data.outstanding_shares_change_percentage_1month ? (data.outstanding_shares_change_percentage_1month > 0 ? '+' : '-') + data.outstanding_shares_change_percentage_1month + '%' : 'No Change'} from last month</p>
            </div>
        </div>

        {/* Card 4: Float */}
        <div className="col-12 col-md-6 col-lg-3 ">
            <div className="metric-card p-3 rounded-4 border shadow-sm custom-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className=" small">Float</span>
                    <img src="/images/MI/activity.svg" alt="Float" width="20" height="20" className="opacity-75" />
                </div>
                <h3 className="h4 mb-1">{formatNumber(free_float)}</h3>
                <p className=" small mb-0">{free_float_percentage} %</p>
            </div>
        </div>
    </div>
    
      <div className="row g-4 mt-4">
        {/* Left Section: Scrollable Table */}
        <div className="col-lg-9">
          <div className="custom-card border-0 shadow-sm rounded-4 p-4 h-100">
            <h5 className="mb-4 fw-normal">Top 20 Entity & Fund Shareholders</h5>
            <div className="table-responsive scrollable-table-container">
              <table className="table align-middle custom-table">
                <thead>
                  <tr>
                    <th>Holder</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th className="pe-4">Shares</th>
                    <th>% Held</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {top_20_investors.map((row, index) => (
                    <tr key={index}>
                      <td className="text-dark">{row.name}</td>
                      <td>
                        <span className="badge-institutional px-2 py-1 rounded">
                          {row.type || 'Institutional'}
                        </span>
                      </td>
                      <td className=" small">{row.date}</td>
                      <td className="text-dark pe-4">{Number(row.shares_held).toLocaleString()}</td>
                      <td className="text-dark">{row.shares_held_percentage}%</td>
                      <td className={row.change >= 0 ? 'text-success' : 'text-danger'}>
                        {(() => {
                            const prev = row.shares_held - row.change;
                            const percent = prev !== 0 ? ((row.change / prev) * 100).toFixed(2) : (row.change > 0 ? 100 : 0);
                            return `${row.change >= 0 ? '↑' : '↓'} ${Math.abs(percent)}%`;
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Section: Company Details */}
        <div className="col-lg-3">
          <div className="custom-card border-0 shadow-sm rounded-4 p-4 h-100">
            <h5 className="mb-4 fw-normal">Company Details</h5>
            <div className="mb-4">
              <label className="detail-label d-block">Industry</label>
              <span className="detail-value">{industry || '-'}</span>
            </div>
            <div className="mb-4">
              <label className="detail-label d-block">Headquarter</label>
              <span className="detail-value">{country_name || '-'}</span>
            </div>
            <div className="mb-4">
              <label className="detail-label d-block">Market Cap</label>
              <span className="detail-value">{formatCurrency(market_cap)}</span>
            </div>
            <div className="mb-0">
              <label className="detail-label d-block">Strategic Ownership</label>
              <span className="detail-value">{strategic_ownership}%</span>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-4 mt-4">
    {/* Left Section: Cumulative Ownership (Matches width of Shareholders table) */}
    <div className="col-lg-9">
      <div className="custom-card border-0 shadow-sm rounded-4 p-4">
        <h5 className="mb-4 fw-normal text-dark">Cumulative Ownership</h5>
        
        <div className="ownership-rows">
            {
                data['10_shares_held'] && (
                  <div className="row align-items-center mb-4 last-child-mb-0">
                      <div className="col-2 text-dark fs-6">Top 10</div>
                      <div className="col-7">
                        <div className="d-flex justify-content-between mb-1">
                          <span className=" extra-small">{formatNumber(data['10_shares_held'])} shares</span>
                          <span className="text-dark small">{calcPercent(data['10_shares_held'])}%</span> 
                        </div>
                        <div className="progress bg-light-grey rounded-pill" style={{ height: '6px' }}>
                          <div className="progress-bar brand-purple-bg" role="progressbar" style={{ width: `${calcPercent(data['10_shares_held'])}%` }} aria-valuenow={calcPercent(data['10_shares_held'])} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                      <div className="col-3 text-end text-dark fs-6">{formatCurrency(data['10_shares_held_value'])}</div>
                  </div>
                )
            }
             {
                data['20_shares_held'] && (
                  <div className="row align-items-center mb-4 last-child-mb-0">
                      <div className="col-2 text-dark fs-6">Top 20</div>
                      <div className="col-7">
                        <div className="d-flex justify-content-between mb-1">
                          <span className=" extra-small">{formatNumber(data['20_shares_held'])} shares</span>
                          <span className="text-dark small">{calcPercent(data['20_shares_held'])}%</span> 
                        </div>
                        <div className="progress bg-light-grey rounded-pill" style={{ height: '6px' }}>
                          <div className="progress-bar brand-purple-bg" role="progressbar" style={{ width: `${calcPercent(data['20_shares_held'])}%` }} aria-valuenow={calcPercent(data['20_shares_held'])} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                      <div className="col-3 text-end text-dark fs-6">{formatCurrency(data['20_shares_held_value'])}</div>
                  </div>
                )
            }
             {
                data['50_shares_held'] && (
                  <div className="row align-items-center mb-4 last-child-mb-0">
                      <div className="col-2 text-dark fs-6">Top 50</div>
                      <div className="col-7">
                        <div className="d-flex justify-content-between mb-1">
                          <span className=" extra-small">{formatNumber(data['50_shares_held'])} shares</span>
                          <span className="text-dark small">{calcPercent(data['50_shares_held'])}%</span> 
                        </div>
                        <div className="progress bg-light-grey rounded-pill" style={{ height: '6px' }}>
                          <div className="progress-bar brand-purple-bg" role="progressbar" style={{ width: `${calcPercent(data['50_shares_held'])}%` }} aria-valuenow={calcPercent(data['50_shares_held'])} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                      <div className="col-3 text-end text-dark fs-6">{formatCurrency(data['50_shares_held_value'])}</div>
                  </div>
                )
            }
             {
                data['100_shares_held'] && (
                  <div className="row align-items-center mb-4 last-child-mb-0">
                      <div className="col-2 text-dark fs-6">Top 100</div>
                      <div className="col-7">
                        <div className="d-flex justify-content-between mb-1">
                          <span className=" extra-small">{formatNumber(data['100_shares_held'])} shares</span>
                          <span className="text-dark small">{calcPercent(data['100_shares_held'])}%</span> 
                        </div>
                        <div className="progress bg-light-grey rounded-pill" style={{ height: '6px' }}>
                          <div className="progress-bar brand-purple-bg" role="progressbar" style={{ width: `${calcPercent(data['100_shares_held'])}%` }} aria-valuenow={calcPercent(data['100_shares_held'])} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                      <div className="col-3 text-end text-dark fs-6">{formatCurrency(data['100_shares_held_value'])}</div>
                  </div>
                )
            }
        </div>
      </div>
    </div>

    {/* Right Section: Ownership Alert (Matches width of Company Details) */}
    <div className="col-lg-3">
      <div className="custom-card border-0 shadow-sm rounded-4 p-4 h-100 alert-box-custom">
        <div className="d-flex align-items-center mb-3">
          <i className="bi bi-info-circle text-warning me-2 fs-5"></i>
          <h6 className="mb-0 fw-normal">Ownership Alert</h6>
        </div>
        <p className=" small lh-lg mb-0">
          {data.shares_held_by_strategic_investors_change_percentage_1month ? (
            <>
              Institutional ownership {data.shares_held_by_strategic_investors_change_percentage_1month > 0 ? "increased" : "decreased"} by 
              <span className={data.shares_held_by_strategic_investors_change_percentage_1month > 0 ? "text-success fw-bold mx-1" : "text-danger fw-bold mx-1"}>
                {Math.abs(data.shares_held_by_strategic_investors_change_percentage_1month)}%
              </span> 
              in the last month, indicating {data.shares_held_by_strategic_investors_change_percentage_1month > 0 ? "growing" : "declining"} confidence.
            </>
          ) : (
            "Institutional ownership remained stable in the last month."
          )}
        </p>
      </div>
    </div>
  </div>
    </div>
    );
};

export default ShareHoldings;